import { API_BASE, LEGACY_STORAGE_KEY } from './constants.js'
import { publishAblyEvent } from '../hooks/useAblyChannel.js'

/**
 * Client for the shared Q2 Goals data store.
 *
 * Endpoints (same Cloudflare Worker the current dashboard uses):
 *   GET  /goals  — returns the full pillars array (or 204 if empty)
 *   POST /goals  — saves the full pillars array
 *
 * Fallback: if the worker is unreachable, read/write localStorage so
 * a user offline still sees something reasonable.
 */

/**
 * Fetch the shared goals data array from the worker.
 * Returns an array of pillar objects, or null if the store is empty.
 */
export async function fetchGoalsData() {
  try {
    const res = await fetch(API_BASE + '/goals')
    if (res.status === 204) return readLocalFallback()
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    if (Array.isArray(data) && data.length > 0) {
      // Keep a local cache for offline fallback
      try {
        localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(data))
      } catch {}
      return data
    }
    return readLocalFallback()
  } catch (err) {
    console.warn('fetchGoalsData: worker unreachable, using local cache', err)
    return readLocalFallback()
  }
}

/**
 * Save the shared goals data array.
 * Always writes to localStorage AND the worker, matching the current
 * dashboard's dual-write pattern.
 */
export async function saveGoalsData(data) {
  const json = JSON.stringify(data)
  // Local cache first (cheap, always succeeds)
  try {
    localStorage.setItem(LEGACY_STORAGE_KEY, json)
  } catch {}
  // Then POST to worker
  const res = await fetch(API_BASE + '/goals', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: json,
  })
  if (!res.ok) throw new Error(`Save failed: HTTP ${res.status}`)
  // Broadcast real-time notification so other clients refetch.
  // Fire-and-forget — if Ably is unavailable, the 15s poll catches the change.
  publishAblyEvent('goals-changed').catch(() => {})
  return data
}

function readLocalFallback() {
  try {
    const saved = localStorage.getItem(LEGACY_STORAGE_KEY)
    if (!saved) return null
    const parsed = JSON.parse(saved)
    return Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}
